import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

import CreateCustomerModal from '../forms/CreateCustomerModal.jsx';
import { customersApiService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';

function TicketInfo() {
  const { id } = useParams();
  const { user } = useAuth();
  const issueWithInputRef = useRef(null);

  const [showCustomerModal, setShowCustomerModal] = useState(false);

  const toastStyles = {
    style: {
      borderRadius: '10px',
      background: 'oklch(96.7% 0.003 264.542)',
      color: '#000',
      borderColor: 'oklch(57.7% 0.245 27.325)',
      borderWidth: '3px',
    },
    duration: 2000,
  };

  const [values, setValues] = useState({
    customer_name: '',
    customer_number: '',
    ticket_number: '',
    issue_with: '',
  });

  const [errors, setErrors] = useState({});

  const validate = () => {
    let newErrors = {};

    if (values.customer_name.length > 50) {
      newErrors.customer_name = 'Customer name must be at most 50 characters.';
    }

    if (!/^100\d{0,7}$|^200\d{0,7}$/.test(values.customer_number)) {
      newErrors.customer_number =
        'Customer number must start with 100 or 200 and be 10 digits long.';
    }

    if (
      !/^(EM|SR|SC).{0,7}$/.test(values.ticket_number.toUpperCase()) ||
      values.ticket_number.length > 10
    ) {
      newErrors.ticket_number =
        'Ticket number must start with EM, SR, or SC and be at most 10 characters.';
    }

    if (values.issue_with.length > 70) {
      newErrors.issue_with = 'Issue description must be at most 70 characters.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (!id) return;

    customersApiService.getTicketById(id)
      .then(async ticket => {
        console.log('✅ Ticket recibido:', ticket);

        // Fetch fresh customer data to avoid stale cached names
        let customerName = ticket.customer_name;
        try {
          const customer = await customersApiService.getCustomerByNumber(ticket.customer_number);
          if (customer && customer.customer_name) {
            customerName = customer.customer_name;
          }
        } catch (err) {
          console.warn('Could not fetch fresh customer data, using cached name:', err);
        }

        setValues({
          customer_name: customerName,
          customer_number: `${ticket.customer_number}`,
          ticket_number: `${ticket.ticket_number}`,
          issue_with: `${ticket.issue_with}`,
        });
      })
      .catch(err => console.error('Error fetching ticket:', err));
  }, [id]);

  // Restore pending ticket form data when returning from customers portal
  useEffect(() => {
    const pendingTicket = localStorage.getItem('pendingTicket');
    if (pendingTicket && !id) {
      try {
        const ticketData = JSON.parse(pendingTicket);

        // Verify customer was created by checking API
        customersApiService.getCustomerByNumber(ticketData.customer_number)
          .then(customer => {
            if (customer) {
              setValues({
                ticket_number: ticketData.ticket_number || '',
                customer_number: ticketData.customer_number || '',
                customer_name: customer.customer_name || '',
                issue_with: ticketData.issue_with || '',
              });

              // Clear localStorage after restoring
              localStorage.removeItem('pendingTicket');

              // Auto-focus on issue field
              setTimeout(() => {
                issueWithInputRef.current?.focus();
              }, 200);

              toast.success('Customer found! Please continue with ticket creation.', toastStyles);
            }
          })
          .catch(err => {
            console.error('Error verifying customer:', err);
            // Customer still not found, clear the pending ticket
            localStorage.removeItem('pendingTicket');
          });
      } catch (err) {
        console.error('Error parsing pending ticket:', err);
        localStorage.removeItem('pendingTicket');
      }
    }
  }, [id]);

  const navigate = useNavigate();

  const handleCustomerNumberChange = async e => {
    const customerNumber = e.target.value;
    setValues({ ...values, customer_number: customerNumber });

    if (/^100\d{0,7}$|^200\d{0,7}$/.test(customerNumber)) {
      try {
        const customer = await customersApiService.getCustomerByNumber(customerNumber);
        if (customer) {
          setValues(prev => ({
            ...prev,
            customer_name: customer.customer_name,
          }));
          // Auto-focus on "Issue with" field after customer name is populated
          setTimeout(() => {
            issueWithInputRef.current?.focus();
          }, 100);
        } else {
          // Customer not found - redirect to external customers portal
          setValues(prev => ({ ...prev, customer_name: '' }));
          handleRedirectToCustomersPortal(customerNumber);
        }
      } catch (error) {
        console.error('Error fetching customer name:', error);
        // Customer not found in external API - redirect to create customer
        setValues(prev => ({ ...prev, customer_name: '' }));
        handleRedirectToCustomersPortal(customerNumber);
      }
    } else {
      setValues(prev => ({ ...prev, customer_name: '' }));
    }
  };

  const handleRedirectToCustomersPortal = (customerNumber) => {
    const confirmed = window.confirm(
      `Customer ${customerNumber} not found. Would you like to create this customer?\n\nYou will be redirected to the Customers Portal.`
    );

    if (confirmed) {
      // Save current form state to localStorage to restore after returning
      localStorage.setItem('pendingTicket', JSON.stringify({
        ticket_number: values.ticket_number,
        customer_number: customerNumber,
        issue_with: values.issue_with,
      }));

      // Redirect to customers portal with customer number pre-filled
      window.location.href = `https://www.customers.nginx.encasa/create-customer?number=${customerNumber}&returnUrl=${encodeURIComponent(window.location.href)}`;
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!validate()) return;

    try {
      // Include user_id from authenticated user when creating ticket
      const ticketData = {
        ...values,
        user_id: user?.id || null,
      };
      const res = await customersApiService.createTicket(ticketData);
      if (res.ticket_id || res.id) {
        toast.success('Ticket created successfully!', toastStyles);
        navigate(`/smart-ticket/${res.ticket_id || res.id}`);
      } else if (res.error) {
        console.log(res.error);
        if (res.error.includes('already exists') || res.error.includes('duplicate')) {
          toast.error(
            'This ticket number already exists. Please use a different ticket number.',
            toastStyles
          );
        } else {
          toast.error(res.error, toastStyles);
        }
      }
    } catch (err) {
      toast.error(
        'An unexpected error occurred. Please try again later.',
        toastStyles
      );
    }
  };

  return (
    <div className="flex items-start pt-4 pl-4">
      <form
        onSubmit={handleSubmit}
        className="bg-red-400 p-4 rounded-xl shadow-xl w-1/2 ml-0"
      >
        <div className="grid grid-cols-6 grid-rows-2 gap-2 font-semibold">
          <div className="col-start-1 col-span-2">
            <label htmlFor="ticket">Ticket Number:</label>
            <input
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-md"
              type="text"
              id="ticket"
              name="ticket"
              value={values.ticket_number}
              onChange={e =>
                setValues({ ...values, ticket_number: e.target.value })
              }
              placeholder="EM/SR/SC"
              required
            ></input>
            {errors.ticket_number && (
              <p className="text-red-600 bg-slate-100 rounded-md p-2 mt-2 border border-red-600">
                {errors.ticket_number}
              </p>
            )}
          </div>
          <div className="col-start-3 col-span-2">
            <label htmlFor="customerNumber">Customer Number:</label>
            <input
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-md"
              type="text"
              id="customerNumber"
              name="customerNumber"
              value={values.customer_number}
              onChange={e =>
                setValues({ ...values, customer_number: e.target.value })
              }
              onBlur={handleCustomerNumberChange}
              placeholder="100/200XXXXX"
              required
            ></input>
            {errors.customer_number && (
              <p className="text-red-600 bg-slate-100 rounded-md p-2 mt-2 border border-red-600">
                {errors.customer_number}
              </p>
            )}
          </div>
          <div className="col-start-5 col-span-2">
            <label htmlFor="customerName">Customer Name:</label>
            <input
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-md"
              type="text"
              id="customerName"
              name="customerName"
              value={values.customer_name}
              readOnly
              placeholder="Customer company name"
              required
            ></input>
            {errors.customer_name && (
              <p className="text-red-600 bg-slate-100 rounded-md p-2 mt-2 border border-red-600">
                {errors.customer_name}
              </p>
            )}
          </div>
          <div className="col-start-1 col-span-6 pb-4">
            <label htmlFor="issueWith">Issue with / Request about:</label>
            <input
              ref={issueWithInputRef}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-md"
              type="text"
              id="issueWith"
              name="issueWith"
              value={values.issue_with}
              onChange={e =>
                setValues({ ...values, issue_with: e.target.value })
              }
              placeholder="max 45, avoid special characters"
              required
            ></input>
            {errors.issue_with && (
              <p className="text-red-600 bg-slate-100 rounded-md p-2 mt-2 border border-red-600">
                {errors.issue_with}
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-between items-center">
          {' '}
          <button className="px-16 py-2 bg-white text-gray-800 font-semibold rounded hover:bg-gray-200 transition border-b-4 border-gray-200 hover:border-gray-300 shadow-md">
            Submit
          </button>
        </div>
      </form>

      <CreateCustomerModal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        customerNumber={values.customer_number}
      />
    </div>
  );
}

export default TicketInfo;
